/**
 * AlertsEngine — Motor de Alertas Proactivas QuiPus
 *
 * Genera alertas contables y tributarias a partir de datos reales.
 * Reglas puras — sin ML, sin modificaciones a datos.
 *
 * Alertas generadas:
 *   SUNAT:    PDT 621 vencimiento, PLE libros pendientes, IGV a favor
 *   CxP:      Facturas vencidas +90 días, próximas a vencer 30 días
 *   CxC:      Clientes con deuda +60 días
 *   Contable: Asientos sin publicar, TX sin clasificar, Detracciones pendientes
 *
 * REGLAS:
 * - assertFirmId en toda query Supabase
 * - Decimal.js para todos los montos
 * - Read-only: NUNCA modifica datos
 */

import Decimal from 'decimal.js';
import { supabase } from './supabaseClient';
import { withSentry } from '../utils/sentryLazy';
import { assertFirmId } from '../utils/assertFirmId';
import { getSunatDeadline } from '../utils/sunatCalendar';
import type { Company, Transaction } from '../types';
import { resumenClasificacion } from '../utils/sinClasificar';
import type { SerializedJournalEntry } from './AccountingEngine';

type AlertLang = 'es' | 'en';

// ─── Tipos públicos ───────────────────────────────────────────────────────────

export interface QuiPusAlert {
  id: string;
  type: 'sunat' | 'cxp' | 'cxc' | 'contable' | 'tributario';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  action?: string;
  actionUrl?: string;
  fecha: string;
  dismissed: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDecimal(n: number | string | null | undefined): Decimal {
  try { return new Decimal(String(n ?? 0)); }
  catch { return new Decimal(0); }
}

function plural(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

// ─── Motor principal ──────────────────────────────────────────────────────────

/**
 * Genera el conjunto completo de alertas activas para la empresa.
 * Solo lee datos — nunca escribe.
 *
 * @param firmId            ID de la firma (auth.uid())
 * @param companyId         UUID de la empresa activa
 * @param company           Datos de la empresa (ruc, periodo, regimen)
 * @param liveTransactions  Transacciones del período activo (desde SyncContext)
 * @param _liveJournalEntries  No usado actualmente — reservado para futura validación doble
 */
export async function generateAlerts(
  firmId: string,
  companyId: string,
  company: Company,
  liveTransactions: Transaction[],
  _liveJournalEntries: SerializedJournalEntry[],
  lang: AlertLang = 'es',
): Promise<QuiPusAlert[]> {
  const fid = assertFirmId(firmId, 'AlertsEngine.generateAlerts');

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];
  const periodo  = company.periodo ?? '';

  const alerts: Omit<QuiPusAlert, 'dismissed'>[] = [];

  // Textos visibles en ES (por defecto) o EN. Solo cambia el texto, nunca la regla.
  const en = lang === 'en';
  const T = (es: string, enText: string): string => (en ? enText : es);
  const fmtDate = (d: Date): string =>
    new Intl.DateTimeFormat(en ? 'en-US' : 'es-PE', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
  const fmtPeriod = (p: string): string =>
    new Intl.DateTimeFormat(en ? 'en-US' : 'es-PE', { month: 'long', year: 'numeric' }).format(new Date(`${p}-01T12:00:00`));

  // ── 1. PDT 621 vencimiento ────────────────────────────────────────────────
  if (company.ruc && periodo) {
    try {
      const dl   = getSunatDeadline(company.ruc, periodo);
      const dias = dl.diasRestantes;

      if (dias <= 7) {
        const severity: QuiPusAlert['severity'] = dias < 3 ? 'critical' : 'warning';
        const dlLabel     = en ? fmtDate(dl.fechaVencimiento) : dl.label;
        const periodoText = en ? fmtPeriod(periodo) : dl.periodoLabel;
        const absDias     = Math.abs(dias);
        const verb = dias < 0
          ? T(`venció hace ${absDias} ${plural(absDias, 'día', 'días')}`, `was due ${absDias} ${plural(absDias, 'day', 'days')} ago`)
          : T(`vence el ${dlLabel}`, `is due on ${dlLabel}`);
        alerts.push({
          id:        `pdt621-${periodo}`,
          type:      'sunat',
          severity,
          title:     dias < 0
            ? T(`PDT 621 VENCIDO — ${periodoText}`, `PDT 621 OVERDUE — ${periodoText}`)
            : T(`PDT 621 en ${dias} ${plural(dias, 'día', 'días')} — ${periodoText}`, `PDT 621 in ${dias} ${plural(dias, 'day', 'days')} — ${periodoText}`),
          message:   T(`PDT 621 de ${periodoText} ${verb}. Faltan ${Math.max(0, dias)} días.`, `PDT 621 for ${periodoText} ${verb}. ${Math.max(0, dias)} days left.`),
          action:    T('Ir a PDT 621', 'Go to PDT 621'),
          actionUrl: '/declaraciones',
          fecha:     todayStr,
        });
      }
    } catch { /* sunatCalendar non-critical */ }
  }

  // ── 2. PLE libros pendientes ──────────────────────────────────────────────
  if (periodo && liveTransactions.length > 0) {
    try {
      const { data: pleData } = await supabase
        .from('sunat_records')
        .select('tipo_libro')
        .eq('firm_id', fid)
        .eq('company_id', companyId)
        .eq('periodo', periodo);

      const exportados = new Set(
        (pleData ?? []).map((r: Record<string, unknown>) => r.tipo_libro as string),
      );
      const requeridos = ['RC', 'RV'];
      const faltantes  = requeridos.filter(t => !exportados.has(t));

      if (faltantes.length > 0) {
        alerts.push({
          id:        `ple-${periodo}`,
          type:      'sunat',
          severity:  'warning',
          title:     T(`Libros PLE pendientes — ${periodo}`, `PLE books pending — ${periodo}`),
          message:   T(`Los libros PLE de ${periodo} no han sido generados. Faltantes: ${faltantes.join(', ')}.`, `The PLE books for ${periodo} have not been generated. Missing: ${faltantes.join(', ')}.`),
          action:    T('Generar PLE', 'Generate PLE'),
          actionUrl: '/export',
          fecha:     todayStr,
        });
      }
    } catch { /* non-critical */ }
  }

  // ── 3. IGV a favor del contribuyente ─────────────────────────────────────
  {
    const igvDebito  = liveTransactions
      .filter(tx => tx.amount > 0)
      .reduce((s, tx) => s.plus(toDecimal(tx.igv)), new Decimal(0));
    const igvCredito = liveTransactions
      .filter(tx => tx.amount < 0)
      .reduce((s, tx) => s.plus(toDecimal(tx.igv).abs()), new Decimal(0));
    const favor = igvCredito.minus(igvDebito);

    if (favor.greaterThan(0) && igvCredito.greaterThan(0)) {
      alerts.push({
        id:       `igv-favor-${periodo}`,
        type:     'tributario',
        severity: 'info',
        title:    T('Saldo a favor de IGV', 'IGV credit balance'),
        message:  T(`Tienes S/ ${favor.toFixed(2)} de saldo a favor de IGV este período.`, `You have S/ ${favor.toFixed(2)} of IGV credit this period.`),
        fecha:    todayStr,
      });
    }
  }

  // ── 4. CxP vencidas +90 días ──────────────────────────────────────────────
  {
    const cutoff90 = new Date(today);
    cutoff90.setDate(cutoff90.getDate() - 90);
    const cutoff90Str = cutoff90.toISOString().split('T')[0];

    const vencidas = liveTransactions.filter(tx =>
      tx.amount < 0 &&
      tx.fechaVencimiento &&
      tx.fechaVencimiento < cutoff90Str,
    );
    if (vencidas.length > 0) {
      const total = vencidas.reduce((s, tx) => s.plus(toDecimal(tx.amount).abs()), new Decimal(0));
      alerts.push({
        id:        `cxp-vencidas-90-${periodo}`,
        type:      'cxp',
        severity:  'critical',
        title:     T(`${vencidas.length} ${plural(vencidas.length, 'factura vencida', 'facturas vencidas')} +90 días`, `${vencidas.length} overdue ${plural(vencidas.length, 'invoice', 'invoices')} +90 days`),
        message:   T(`${vencidas.length} ${plural(vencidas.length, 'factura lleva', 'facturas llevan')} más de 90 días sin pagar. Total: S/ ${total.toFixed(2)}.`, `${vencidas.length} ${plural(vencidas.length, 'invoice has', 'invoices have')} been unpaid for more than 90 days. Total: S/ ${total.toFixed(2)}.`),
        action:    T('Ver Cuentas por Pagar', 'View Accounts Payable'),
        actionUrl: '/cxp',
        fecha:     todayStr,
      });
    }
  }

  // ── 5. CxP próximas a vencer 30 días ─────────────────────────────────────
  {
    const ahead30 = new Date(today);
    ahead30.setDate(ahead30.getDate() + 30);
    const ahead30Str = ahead30.toISOString().split('T')[0];

    const proximas = liveTransactions.filter(tx =>
      tx.amount < 0 &&
      tx.fechaVencimiento &&
      tx.fechaVencimiento >= todayStr &&
      tx.fechaVencimiento <= ahead30Str,
    );
    if (proximas.length > 0) {
      const total = proximas.reduce((s, tx) => s.plus(toDecimal(tx.amount).abs()), new Decimal(0));
      alerts.push({
        id:        `cxp-proximas-30-${periodo}`,
        type:      'cxp',
        severity:  'warning',
        title:     T(`${proximas.length} ${plural(proximas.length, 'factura vence', 'facturas vencen')} en 30 días`, `${proximas.length} ${plural(proximas.length, 'invoice', 'invoices')} due in 30 days`),
        message:   T(`${proximas.length} ${plural(proximas.length, 'factura vence', 'facturas vencen')} en los próximos 30 días. Total: S/ ${total.toFixed(2)}.`, `${proximas.length} ${plural(proximas.length, 'invoice is', 'invoices are')} due within the next 30 days. Total: S/ ${total.toFixed(2)}.`),
        action:    T('Ver Cuentas por Pagar', 'View Accounts Payable'),
        actionUrl: '/cxp',
        fecha:     todayStr,
      });
    }
  }

  // ── 6. CxC clientes con deuda +60 días ───────────────────────────────────
  {
    const cutoff60 = new Date(today);
    cutoff60.setDate(cutoff60.getDate() - 60);
    const cutoff60Str = cutoff60.toISOString().split('T')[0];

    const cxcVencidas = liveTransactions.filter(tx =>
      tx.amount > 0 &&
      tx.fechaVencimiento &&
      tx.fechaVencimiento < cutoff60Str,
    );
    if (cxcVencidas.length > 0) {
      const total    = cxcVencidas.reduce((s, tx) => s.plus(toDecimal(tx.amount)), new Decimal(0));
      const clientes = new Set(
        cxcVencidas.map(tx => {
          const txR = tx as unknown as Record<string, unknown>;
          return (txR['rucCliente'] as string | undefined) ||
                 (txR['nombreCliente'] as string | undefined) ||
                 tx.description;
        }),
      ).size;
      alerts.push({
        id:        `cxc-vencidas-60-${periodo}`,
        type:      'cxc',
        severity:  'warning',
        title:     T(`${clientes} ${plural(clientes, 'cliente con deuda', 'clientes con deuda')} +60 días`, `${clientes} ${plural(clientes, 'customer', 'customers')} with debt +60 days`),
        message:   T(`${clientes} ${plural(clientes, 'cliente tiene', 'clientes tienen')} facturas pendientes de cobro por más de 60 días. Total: S/ ${total.toFixed(2)}.`, `${clientes} ${plural(clientes, 'customer has', 'customers have')} invoices pending collection for more than 60 days. Total: S/ ${total.toFixed(2)}.`),
        action:    T('Ver Cuentas por Cobrar', 'View Accounts Receivable'),
        actionUrl: '/cxc',
        fecha:     todayStr,
      });
    }
  }

  // ── 7. Asientos sin publicar ──────────────────────────────────────────────
  try {
    const { count, error } = await supabase
      .from('journal_entries')
      .select('*', { count: 'exact', head: true })
      .eq('firm_id', fid)
      .eq('company_id', companyId)
      .eq('posted', false)
      .neq('is_deleted', true);

    if (error) {
      // No relanzamos — esta alerta es best-effort, no debe tumbar el resto
      // del panel. Pero antes esto era invisible del todo: solo se leía
      // `count`, nunca `error` — un 503/timeout de Supabase (el cliente
      // resuelve normal, no lanza) se interpretaba como "0 asientos sin
      // publicar" en vez de "no se pudo consultar". Auditoría 2026-09-14:
      // 2 HEAD 503 reales contra journal_entries durante pruebas en vivo,
      // sin ningún rastro en logs/Sentry porque este catch nunca los veía.
      console.error('[AlertsEngine] asientos sin publicar — query falló:', error.message);
      withSentry((S) => S.captureException(new Error(`[AlertsEngine] journal_entries HEAD query failed: ${error.message}`), {
        tags: { operation: 'alerts_unposted_je', firm_id: fid },
        level: 'warning',
      }));
    } else if ((count ?? 0) > 0) {
      const n = count!;
      alerts.push({
        id:        `je-unposted-${periodo}`,
        type:      'contable',
        severity:  'warning',
        title:     T(`${n} ${plural(n, 'asiento sin publicar', 'asientos sin publicar')}`, `${n} unposted ${plural(n, 'entry', 'entries')}`),
        message:   T(`${n} ${plural(n, 'asiento está', 'asientos están')} pendiente${plural(n, '', 's')} de publicar a los libros.`, `${n} ${plural(n, 'entry is', 'entries are')} pending posting to the books.`),
        action:    T('Ir a Asientos Manuales', 'Go to Manual Entries'),
        actionUrl: '/journals',
        fecha:     todayStr,
      });
    }
  } catch (err) {
    console.error('[AlertsEngine] asientos sin publicar — excepción:', err);
    withSentry((S) => S.captureException(err, {
      tags: { operation: 'alerts_unposted_je', firm_id: fid },
      level: 'warning',
    }));
  }

  // ── 8. Transacciones sin clasificar ──────────────────────────────────────
  // Definición única (utils/sinClasificar.ts): activa y status ≠ approved, con desglose por causa.
  {
    const r = resumenClasificacion(liveTransactions);
    if (r.sinClasificar > 0) {
      const n = r.sinClasificar;
      const { sin_cuenta: sc, baja_confianza: bc, auto_sin_confirmar: asc } = r.porCausa;
      alerts.push({
        id:        `tx-unclassified-${periodo}`,
        type:      'contable',
        severity:  'warning',
        title:     T(`${n} ${plural(n, 'transacción sin clasificar', 'transacciones sin clasificar')}`, `${n} unclassified ${plural(n, 'transaction', 'transactions')}`),
        message:   T(
          `${n} ${plural(n, 'transacción pendiente', 'transacciones pendientes')} de confirmar: ${sc} sin cuenta, ${bc} de baja confianza, ${asc} sin confirmar.`,
          `${n} ${plural(n, 'transaction', 'transactions')} awaiting confirmation: ${sc} without account, ${bc} low confidence, ${asc} unconfirmed.`,
        ),
        action:    T('Ir al Panel', 'Go to Dashboard'),
        actionUrl: '/dashboard',
        fecha:     todayStr,
      });
    }
  }

  // ── 9. Detracciones pendientes de depósito ────────────────────────────────
  {
    const detPendientes = liveTransactions.filter(tx => {
      const txR = tx as unknown as Record<string, unknown>;
      const monto  = Number(txR['detraccionMonto'] ?? 0);
      const fecha  = txR['detraccionFecha'] as string | undefined;
      return monto > 0 && !fecha;
    });

    if (detPendientes.length > 0) {
      const total = detPendientes.reduce((s, tx) => {
        const txR = tx as unknown as Record<string, unknown>;
        return s.plus(toDecimal(txR['detraccionMonto'] as number | undefined));
      }, new Decimal(0));
      const n = detPendientes.length;
      alerts.push({
        id:        `detracciones-${periodo}`,
        type:      'contable',
        severity:  'critical',
        title:     T(`${n} ${plural(n, 'detracción pendiente', 'detracciones pendientes')} de depósito`, `${n} SPOT ${plural(n, 'deposit', 'deposits')} pending`),
        message:   T(`${n} ${plural(n, 'detracción pendiente', 'detracciones pendientes')} de depósito en Banco de la Nación. Total: S/ ${total.toFixed(2)}.`, `${n} SPOT ${plural(n, 'deposit', 'deposits')} pending at Banco de la Nación. Total: S/ ${total.toFixed(2)}.`),
        action:    T('Ir a Detracciones SPOT', 'Go to SPOT Withholdings'),
        actionUrl: '/detracciones',
        fecha:     todayStr,
      });
    }
  }

  // All alerts start un-dismissed (AlertsContext applies dismissed state)
  return alerts.map(a => ({ ...a, dismissed: false }));
}
